// Today — the screen the app is judged on.
//
// It answers, in order: what do I need to do today, what is running, how am I
// tracking, and has anything changed enough to matter (brief §2). Anything that is
// not one of those four answers belongs on another screen.

import { useCallback, useEffect, useState } from 'react';
import { api, type TodayAction, type TodaySummary } from '../lib/api.ts';
import { Button, Card, DemoBadge, EmptyState, ErrorNote, SectionTitle, Spinner } from '../components/ui.tsx';
import { RedFlagBanner } from '../components/RedFlagBanner.tsx';
import { relativeDate, titleCase, weekdayDate } from '../lib/format.ts';

export function Today({ navigate }: { navigate: (path: string) => void }) {
  const [data, setData] = useState<TodaySummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.today());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    // Coming back to the app after acting on a notification should show the result,
    // not the state from when it was last backgrounded.
    const onVisible = (): void => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const act = useCallback(async (action: TodayAction, kind: 'done' | 'snooze' | 'skip') => {
    setBusy(action.occurrence_id);
    try {
      await api.reminders.act(action.occurrence_id, { action: kind });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [load]);

  if (error && !data) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const outstanding = data.actions.filter((a) => a.status === 'pending' || a.status === 'snoozed');
  const settled = data.actions.filter((a) => a.status !== 'pending' && a.status !== 'snoozed');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{data.greeting}</h1>
        <p className="text-sm text-muted mt-0.5">{weekdayDate(data.date)}</p>
      </header>

      {data.red_flags.length > 0 && <RedFlagBanner flags={data.red_flags} onChange={load} />}
      {error && <ErrorNote message={error} />}

      <section>
        <SectionTitle>Today</SectionTitle>
        {data.actions.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing scheduled"
              detail="Start an experiment and its reminders will appear here."
              action={<Button variant="primary" onClick={() => navigate('/experiments/new')}>New experiment</Button>}
            />
          </Card>
        ) : (
          <ul className="space-y-2">
            {outstanding.map((action) => (
              <ActionCard key={action.occurrence_id} action={action} busy={busy === action.occurrence_id}
                onAct={(kind) => act(action, kind)} onOpen={() => openAction(action, navigate)} />
            ))}
            {settled.length > 0 && (
              <li className="pt-1">
                <details>
                  <summary className="text-xs text-muted cursor-pointer min-h-11 flex items-center">
                    {settled.length} already dealt with
                  </summary>
                  <ul className="space-y-2 mt-2">
                    {settled.map((action) => (
                      <ActionCard key={action.occurrence_id} action={action} busy={false}
                        onAct={(kind) => act(action, kind)} onOpen={() => openAction(action, navigate)} />
                    ))}
                  </ul>
                </details>
              </li>
            )}
          </ul>
        )}
      </section>

      {data.experiments.length > 0 && (
        <section>
          <SectionTitle
            action={<button className="text-xs text-accent min-h-11" onClick={() => navigate('/experiments')}>All</button>}
          >
            Active experiments
          </SectionTitle>
          <ul className="space-y-3">
            {data.experiments.map((experiment) => (
              <li key={experiment.id}>
                <Card as="article" className="space-y-3">
                  <button className="w-full text-left" onClick={() => navigate(`/experiments/${experiment.id}`)}>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium leading-snug">
                        {experiment.name.replace(/^\[demo\]\s*/, '')}
                      </h3>
                      {experiment.name.startsWith('[demo]') && <DemoBadge />}
                    </div>
                    <p className="text-xs text-muted mt-1">
                      {experiment.day_number !== null && experiment.total_days !== null
                        ? `Day ${experiment.day_number} of ${experiment.total_days}`
                        : titleCase(experiment.status)}
                      {experiment.adherence_percent !== null && ` · Adherence ${experiment.adherence_percent}%`}
                    </p>
                    <p className="text-sm mt-2">{experiment.primary_metric?.headline}</p>
                  </button>

                  {experiment.pending_question && (
                    <button
                      onClick={() => navigate(`/experiments/${experiment.id}`)}
                      className="w-full text-left rounded-xl bg-accent-soft border border-accent/30 px-3 py-2.5"
                    >
                      <span className="block text-[10px] uppercase tracking-wide text-muted font-semibold">Question</span>
                      <span className="block text-sm mt-0.5">{experiment.pending_question.question}</span>
                    </button>
                  )}

                  {!experiment.checkin_done && experiment.status !== 'paused' && (
                    <Button variant="primary" full onClick={() => navigate(`/experiments/${experiment.id}/checkin`)}>
                      Check in — under 30 seconds
                    </Button>
                  )}
                  {experiment.checkin_done && (
                    <p className="text-xs text-good">✓ Checked in today</p>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.upcoming.length > 0 && (
        <section>
          <SectionTitle>Coming up</SectionTitle>
          <Card className="divide-y divide-hairline p-0">
            {data.upcoming.map((item, i) => (
              <div key={`${item.type}-${i}`} className="px-4 py-3 flex items-baseline justify-between gap-3">
                <div>
                  <p className="text-sm">{item.title.replace(/^\[demo\]\s*/, '')}</p>
                  {item.detail && <p className="text-xs text-muted mt-0.5">{item.detail}</p>}
                </div>
                <span className="text-xs text-muted shrink-0">{relativeDate(item.date, data.date)}</span>
              </div>
            ))}
          </Card>
        </section>
      )}

      {data.recent_events.length > 0 && (
        <section>
          <SectionTitle
            action={<button className="text-xs text-accent min-h-11" onClick={() => navigate('/timeline')}>Timeline</button>}
          >
            Recently
          </SectionTitle>
          <Card className="divide-y divide-hairline p-0">
            {data.recent_events.slice(0, 4).map((event) => (
              <div key={event.id} className="px-4 py-3 flex items-baseline justify-between gap-3">
                <p className="text-sm">{event.title.replace(/^\[demo\]\s*/, '')}</p>
                <span className="text-xs text-muted shrink-0">{relativeDate(event.event_date, data.date)}</span>
              </div>
            ))}
          </Card>
        </section>
      )}
    </div>
  );
}

function openAction(action: TodayAction, navigate: (path: string) => void): void {
  if (action.kind === 'checkin' && action.experiment_id) {
    navigate(`/experiments/${action.experiment_id}/checkin`);
  } else if (action.experiment_id) {
    navigate(`/experiments/${action.experiment_id}`);
  }
}

function ActionCard({ action, busy, onAct, onOpen }: {
  action: TodayAction;
  busy: boolean;
  onAct: (kind: 'done' | 'snooze' | 'skip') => void;
  onOpen: () => void;
}) {
  const settled = action.status !== 'pending' && action.status !== 'snoozed';

  return (
    <li className={`card p-3 ${settled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <span className="text-sm tabular-nums text-muted w-12 shrink-0 pt-0.5">{action.due_time}</span>
        <button className="flex-1 text-left min-w-0" onClick={onOpen}>
          <p className="font-medium leading-snug">{action.title}</p>
          {/* The dose the user recorded themselves — shown so they can follow their
              own plan without opening the packet. The app never supplies one. */}
          {action.dose && <p className="text-xs text-ink-2 mt-0.5">{action.dose}</p>}
          {/* Instructions show alongside a dose, not instead of it: they carry the
              technique checklist, which is exactly what you want in front of you at
              the moment you take the dose. */}
          {action.instructions && (
            <p className="text-xs text-muted mt-0.5 whitespace-pre-line line-clamp-3">{action.instructions}</p>
          )}
          {action.overdue && !settled && <p className="text-xs text-serious mt-1">Overdue</p>}
          {settled && (
            <p className="text-xs mt-1">
              {action.status === 'done' && <span className="text-good">✓ Done</span>}
              {action.status === 'skipped' && <span className="text-muted">Skipped</span>}
              {action.status === 'missed' && <span className="text-muted">Missed</span>}
            </p>
          )}
        </button>
      </div>

      {!settled && (
        <div className="flex gap-2 mt-2.5">
          <Button variant="primary" className="flex-1" disabled={busy} onClick={() => onAct('done')}>Done</Button>
          <Button disabled={busy} onClick={() => onAct('snooze')}>Snooze</Button>
          <Button variant="ghost" disabled={busy} onClick={() => onAct('skip')}>Skip</Button>
        </div>
      )}
    </li>
  );
}
